import { useContext, useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Form, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useDeepEffect } from '@domain-group/react-hooks'

import UserSearch from '@components/UserSearch'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ShareButton from './components/ShareButton'

import useSelectSMSRecipientModal from './hooks/useSelectSMSRecipientModal'

const MODAL_KEY = 'SelectSMSRecipientModal'

export type ModalCallbacks = {
  closeModal: () => void,
  shareMobileLink: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const SelectSMSRecipientModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    activityKey, campaign, defaultSubject, endPoint, linkAnchor, preSelectedUsers,
  } = modalPayload

  const hookPayload = useSelectSMSRecipientModal()
  const {
    callbacks: {
      setModalState,
      toggleUser,
    },
    filterString,
    hasSelectedUsers,
    images,
    message,
    selectedUsers,
  } = hookPayload

  useDeepEffect(() => {
    if (preSelectedUsers){
      setModalState({ selectedUsers: preSelectedUsers })
    }
  }, [preSelectedUsers])

  useEffect(() => {
    if (defaultSubject){
      // @ts-expect-error TS2353
      setModalState({ subject: defaultSubject })
    }
  }, [defaultSubject])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Send" titleSecondLine="SMS" />

      <SidebarModal.Content>
        <Form.Field label="Add Recipient">
          <UserSearch
            callbacks={{
              onChange: value => setModalState({ filterString: value }),
              selectItem: value => toggleUser(value),
            }}
            endpoint={endPoint}
            secondaryTextProperty="mobile_number"
            // @ts-expect-error TS2339
            selectedIds={selectedUsers.map(user => user.id)}
            value={filterString}
          />
        </Form.Field>

        {hasSelectedUsers && (
          <>
            <Form.Field direction="column" label="Selected Recipients" marginTop="large">
              {selectedUsers.map((user) => {
                const { id, full_name, mobile_number } = user
                // @ts-expect-error TS2339
                const image = images[user.image_id] || {}

                return (
                  <SidebarModal.User
                    disableHover
                    // @ts-expect-error TS2322
                    imageUrl={image.square_url || null}
                    key={id}
                    title={full_name}
                    subtitle={mobile_number}
                  >
                    <Link greyLink onClick={() => toggleUser(user)} textProps={{ fontSize: 'small' }}>
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Link>
                  </SidebarModal.User>
                )
              })}
            </Form.Field>

            <Form.Field direction="column" label="Message">
              <textarea
                onChange={e => setModalState({ message: e.target.value })}
                style={{ height: 80, resize: 'vertical' }}
                value={message}
              />
            </Form.Field>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <ShareButton
          activityKey={activityKey}
          callbacks={callbacks}
          campaign={campaign}
          hookPayload={hookPayload}
          linkAnchor={linkAnchor}
        />
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SelectSMSRecipientModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
